import { Fragment, type ReactNode } from 'react';
import { Link } from 'react-router';
import type { AdminUser, RoomRole, Romvalg } from '../../api/useAdminUsers.ts';
import { romIkon, romNavn, type Rom } from '../../skall/rom.ts';
import { useSmal } from '../../skall/useSmalSkjerm.ts';
import { Ikon } from '../../status/Ikon.tsx';
import { ALLE_IKON, grupper, HYPOTESER_ROM, INGEN_IKON, ROLLE, ROMROLLE, statuslinje, statusFor } from './modell.ts';
import { Radiogruppe } from './Radiogruppe.tsx';

/**
 * Byggeklossene de tre sidene deler (M4.8 steg 3). Formen er konseptets (panelbrukere-konsept
 * v0.2, DD-71); reglene og ordene er wireframens (`admin-panelbrukere.jsx`).
 */

/**
 * Sidens topp over 900 px: veien tilbake som lenke (ett steg opp), h1, ingressen (summen — ikke
 * en setning) og handlingen. Under 900 px bærer stripen veien tilbake og sidens navn (DD-01
 * presisert), så lenken og handlingen står ikke her; h1 står, men i body-størrelse.
 */
export function Topp({ tilbake, tittel, ingress, handling, skjultTittel = false }: { tilbake: { til: string; href: string }; tittel: string; ingress?: ReactNode; handling?: ReactNode; skjultTittel?: boolean }) {
  const smal = useSmal();
  return (
    <div className="panel-pb-topp">
      {!smal ? (
        <Link to={tilbake.href} className="panel-knapp-lenke panel-pb-tilbake">
          <Ikon navn="chevron-back-outline" />
          {tilbake.til}
        </Link>
      ) : null}
      <div className="panel-pb-hode">
        <div className="panel-pb-hode-tekst">
          {/* Under 900 px sier stripen sidens navn; overskriften står for skjermleseren (DD-01). */}
          <h1 className={smal && skjultTittel ? 'panel-skjult' : 'panel-h1 panel-pb-h1'}>{tittel}</h1>
          {ingress ? <div className="panel-pb-ingress">{ingress}</div> : null}
        </div>
        {!smal && handling ? <div className="panel-pb-hode-handling">{handling}</div> : null}
      </div>
    </div>
  );
}

/** Statusen som radens ENE meta-linje, i small (DD-67). Vanlig linjeflyt: brekker som tekst, aldri etter ikonet. */
export function StatusOrd({ bruker, deg = false }: { bruker: AdminUser; deg?: boolean }) {
  const s = statuslinje(bruker);
  return (
    <span className="panel-pb-status">
      <Ikon navn={s.ikon} /> {s.tekst}
      {deg ? ' · Deg — en annen eier endrer tilgangen din' : null}
    </span>
  );
}

export function RolleOrd({ bruker }: { bruker: AdminUser }) {
  const r = bruker.role === 'eier' ? ROLLE.eier : ROLLE.medlem;
  return (
    <span className="panel-pb-rolle">
      <Ikon navn={r.ikon} />
      {r.ord}
    </span>
  );
}

/**
 * Tilgangslinjen: én gruppert linje per person, etter rolle, med rollens ikon og rollen halvfet.
 * En gruppe brekker ALDRI inne i seg — linjen brekker bare mellom gruppene (DD-57-raden i
 * konseptet), og skilletegnet «·» følger gruppen etter. Med ett rom er den bare ordet.
 */
export function TilgangLinje({ bruker, rommene, forst = null, testid }: { bruker: AdminUser; rommene: readonly Rom[]; forst?: Rom | null; testid?: string }) {
  const flere = rommene.length > 1;
  if (bruker.role === 'eier') {
    return (
      <span className="panel-pb-tilgang" data-testid={testid}>
        <span className="panel-pb-grp">
          <Ikon navn={ALLE_IKON} />
          {flere ? 'Alle rom' : 'Alt'}
        </span>
      </span>
    );
  }
  const g = grupper(Object.fromEntries(bruker.roomAccess.map((r) => [r.room, r.role])), rommene, forst);
  if (!g.length) {
    return (
      <span className="panel-pb-tilgang panel-pb-tilgang-ingen" data-testid={testid}>
        <span className="panel-pb-grp">
          <Ikon navn={INGEN_IKON} />
          {flere ? 'Ingen rom ennå' : 'Ingen tilgang ennå'}
        </span>
      </span>
    );
  }
  return (
    <span className="panel-pb-tilgang" data-testid={testid}>
      {g.map((x, i) => (
        <Fragment key={x.rolle}>
          {/* Mellomrommet står UTENFOR gruppen: det er der linjen får brekke. */}
          {i > 0 ? ' ' : null}
          <span className="panel-pb-grp">
            {i > 0 ? <span className="panel-pb-prikk">· </span> : null}
            <Ikon navn={ROMROLLE[x.rolle].ikon} />
            <b>{ROMROLLE[x.rolle].ord}</b>
            {flere ? ` i ${x.rom.map(romNavn).join(', ')}` : null}
          </span>
        </Fragment>
      ))}
    </span>
  );
}

/** Konsekvenssetningen — beslutningsgrunnlag, derfor body (DD-67). Varselet har ikon og tekst (DD-08). */
export function Setning({ s }: { s: { ikon: string; tekst: string; varsel?: true } }) {
  return (
    <p className={s.varsel ? 'panel-pb-setning panel-pb-setning-varsel' : 'panel-pb-setning'} data-testid="pb-setning" data-varsel={s.varsel ? 'true' : undefined}>
      <Ikon navn={s.ikon} />
      <span>{s.tekst}</span>
    </p>
  );
}

/** Én rad på personsiden: navnet til venstre og valget til høyre over 900 px; stablet under. */
export function PersonRad({ navn, ikon, children }: { navn: string; ikon?: string; children: ReactNode }) {
  return (
    <div className="panel-pb-prad">
      <span className="panel-pb-prad-navn">
        {ikon ? <Ikon navn={ikon} /> : null}
        {navn}
      </span>
      <div className="panel-pb-prad-valg">{children}</div>
    </div>
  );
}

type Rommvalg = RoomRole | 'ingen';

/**
 * Rolle øverst, og for et medlem ett rom per rad (aldri kolonner — rom fem legger seg under rom
 * fire). Med ett rom heter raden «Tilgang», og for en ny person finnes ikke «Ingen» der:
 * kontrakten tar én rolle ved opprettelse. Med Eier valgt finnes ingen rom-rad — tilgangen
 * følger av rollen.
 */
export function Tilgangsvalg({ eier, valg, rommene, onRolle, onRom, laast = false, grunnId, ny = false }: { eier: boolean; valg: Romvalg; rommene: readonly Rom[]; onRolle: (eier: boolean) => void; onRom: (rom: Rom, rolle: RoomRole | null) => void; laast?: boolean; grunnId?: string; ny?: boolean }) {
  const flere = rommene.length > 1;
  // Rangert lavest først, som rollene er (CLAUDE.md «Roller»). `bidragsyter` finnes bare i rommet
  // Hypoteser (H1.3; 422 `validation_failed` i Innspill), og står der som fjerde valg (H2.3,
  // produkteier 2026-10-08: i dagens velger, uten ny wireframe).
  const romValg = (r: Rom): Array<{ verdi: Rommvalg; ord: string }> => [
    ...(ny && !flere ? [] : [{ verdi: 'ingen' as const, ord: 'Ingen' }]),
    ...(r === HYPOTESER_ROM ? [{ verdi: 'bidragsyter' as const, ord: ROMROLLE.bidragsyter.ord }] : []),
    { verdi: 'leser', ord: ROMROLLE.leser.ord },
    { verdi: 'behandler', ord: ROMROLLE.behandler.ord },
  ];
  return (
    <>
      <PersonRad navn="Rolle">
        <Radiogruppe
          navn="Rolle"
          valg={[
            { verdi: 'medlem', ord: ROLLE.medlem.ord },
            { verdi: 'eier', ord: ROLLE.eier.ord },
          ]}
          aktiv={eier ? 'eier' : 'medlem'}
          onVelg={(v) => onRolle(v === 'eier')}
          laast={laast}
          grunnId={grunnId}
          fyll
        />
      </PersonRad>
      {!eier ? (
        <>
          {flere ? <h2 className="panel-pb-seksjon">Tilgang per rom</h2> : null}
          {rommene.map((r) => (
            <PersonRad key={r} navn={flere ? romNavn(r) : 'Tilgang'} ikon={flere ? romIkon(r) : undefined}>
              <Radiogruppe navn={flere ? `Tilgang i ${romNavn(r)}` : 'Tilgang'} valg={romValg(r)} aktiv={valg[r] ?? 'ingen'} onVelg={(v) => onRom(r, v === 'ingen' ? null : v)} laast={laast} grunnId={grunnId} fyll />
            </PersonRad>
          ))}
        </>
      ) : null}
    </>
  );
}

/** Status for raden: den deaktiverte er dempet, med tilgangen som sto. */
export const erUte = (b: AdminUser) => statusFor(b) === 'ute';
